import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FaPhoneAlt, FaTimes } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { audio_modal } from "../../Pages/actions/video_audio_modal";
import socketModel from "../../models/socketModel";
import { useNavigate } from "react-router-dom";
import methodModel from "../../methods/methods";
const AudioCallRingModal = ({ isOpen, setIsOpen, roomId, CallingUserData = {}, onAcceptCustom = null }) => {
    const history = useNavigate();
    const user = useSelector(state => state.user);
    const [RoomDetails, setRoomDetails] = useState({});
    const dispatch = useDispatch();
    const chats = useSelector((state) => state.chats?.data);

    const acceptedRef = React.useRef(false);

    useEffect(() => {
        if (chats && roomId) {
            setRoomDetails(chats?.find((_data) => String(_data?.room_id) === String(roomId) || String(_data?._id) === String(roomId)));
        }
        if (isOpen) {
            acceptedRef.current = false;
        }
    }, [roomId, chats, isOpen]);

    const closeModal = () => {
        localStorage.removeItem("BackToVoiceCall");
        if (!acceptedRef.current) {
            const isGroupCall = Boolean(RoomDetails?.isGroupChat || RoomDetails?.groupType || RoomDetails?.group_type || RoomDetails?.type === 'group' || (Array.isArray(RoomDetails?.room_members) && RoomDetails.room_members.length > 2) || CallingUserData?.isGroupChat);
            socketModel.emit('stop-voice-call', {
                room_id: RoomDetails?.room_id || roomId,
                user_id: user?.id || user?._id,
                isDecline: true,
                isOwner: !isGroupCall
            });
        }
        setIsOpen(false);
        dispatch(audio_modal(false));
    };

    const acceptCall = () => {
        acceptedRef.current = true;
        let value = {
            room_id: roomId,
            voiceCall: true,
        };
        localStorage.setItem("BackToVoiceCall", JSON.stringify(value));
        setIsOpen(false);
        dispatch(audio_modal(false));
        if (onAcceptCustom) {
            onAcceptCustom(roomId);
        } else {
            history(`/chat?room_idd=${roomId}&voiceCall=true`);
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[9999]" onClose={closeModal}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-xl" />
                </Transition.Child>

                <div className="fixed inset-0 flex items-center justify-center p-4 z-[10000]">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-3xl bg-slate-900/90 p-7 text-center shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] backdrop-blur-2xl border border-white/10 text-white transition-all">
                            {/* Call Header badge */}
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-semibold mb-5">
                                <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse"></span>
                                Incoming Audio Call
                            </div>

                            {/* Avatar with sonar pulse animation */}
                            <div className="relative w-28 h-28 mx-auto mb-5 flex items-center justify-center">
                                <span className="absolute inset-0 rounded-full bg-indigo-500/30 animate-ping opacity-75"></span>
                                <span className="absolute -inset-3 rounded-full bg-indigo-500/20 animate-pulse blur-sm"></span>
                                <img
                                    src={methodModel.userImg(CallingUserData?.user_image)}
                                    className="relative w-24 h-24 rounded-full object-cover border-4 border-indigo-500 shadow-xl shadow-indigo-500/30"
                                    alt="Caller"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/96?text=User'; }}
                                />
                            </div>

                            <h4 className="text-xl font-bold text-white mb-1 tracking-tight">
                                {CallingUserData?.name || (Boolean(RoomDetails?.isGroupChat || RoomDetails?.groupType || RoomDetails?.group_type || RoomDetails?.type === 'group' || (Array.isArray(RoomDetails?.room_members) && RoomDetails.room_members.length > 2) || CallingUserData?.isGroupChat) ? (RoomDetails?.room_name || CallingUserData?.room_name || "Group Call") : "Unknown User")}
                            </h4>
                            <p className="text-xs text-indigo-300 font-medium mb-8 flex items-center justify-center gap-1.5">
                                <FaPhoneAlt className="w-3 h-3 text-indigo-400 animate-pulse" />
                                {Boolean(RoomDetails?.isGroupChat || RoomDetails?.groupType || RoomDetails?.group_type || RoomDetails?.type === 'group' || (Array.isArray(RoomDetails?.room_members) && RoomDetails.room_members.length > 2) || CallingUserData?.isGroupChat)
                                    ? `Voice call in ${RoomDetails?.room_name || CallingUserData?.room_name || 'group'}...`
                                    : "Voice call request..."}
                            </p>

                            <div className="flex items-center justify-center gap-4">
                                <button
                                    className="flex-1 flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-rose-600 to-red-600 px-5 py-3.5 text-white font-semibold shadow-lg shadow-rose-600/30 hover:from-rose-500 hover:to-red-500 hover:scale-105 active:scale-95 transition-all duration-200 cursor-pointer"
                                    onClick={closeModal}
                                >
                                    <FaTimes className="w-4 h-4" />
                                    Decline
                                </button>
                                <button
                                    className="flex-1 flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-green-600 px-5 py-3.5 text-white font-semibold shadow-lg shadow-emerald-500/30 hover:from-emerald-400 hover:to-green-500 hover:scale-105 active:scale-95 transition-all duration-200 cursor-pointer"
                                    onClick={acceptCall}
                                >
                                    <FaPhoneAlt className="w-4 h-4" />
                                    Accept
                                </button>
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    );
};

export default AudioCallRingModal;
